// ./packages/ui/src/form/dateTimeFieldRules.ts
//
// The pure half of `DateTimeField`: its locale-ordered date part, the day marking (ROADMAP A2e #94, #137) and "is it in
// the past" — no React, no CSS, so the guards RUN it (`check-event-deadline`, `check-event-zone`). The marked day's LOOK
// is `DateTimeField.module.css`.
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { clockFormat } from '@em/utils';

dayjs.extend(utc);
dayjs.extend(timezone);

// Does the user's locale put the month before the day? (US/Canada: yes; most of
// Europe: no.) Detected once from Intl part order.
const localeMonthFirst = (() => {
  try {
    const parts = new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).formatToParts(
      new Date(2000, 0, 2),
    );
    const mi = parts.findIndex((p) => p.type === 'month');
    const di = parts.findIndex((p) => p.type === 'day');
    return mi !== -1 && di !== -1 ? mi < di : true;
  } catch {
    return true;
  }
})();
// Month-name date so there's never DD/MM vs MM/DD ambiguity, ordered by locale.
export const DATE_PART = localeMonthFirst ? 'MMM D, YYYY' : 'D MMM YYYY';

/** Calendar days ("YYYY-MM-DD", inclusive) the picker marks — e.g. the event's own day(s) under a deadline picker. */
export interface MarkedDays {
  from: string;
  /** The last marked day; absent / null = `from` only. */
  to?: string | null;
}

/**
 * ROADMAP A2e #94 — "Oct 10, 2026", or "Oct 10, 2026 – Oct 12, 2026" for a span, in the same locale order as the field.
 * For the sentence a caller puts under a picker that marks those days ("The event is on …"). Empty for no day.
 */
export const formatMarkedDays = (days: MarkedDays | null | undefined): string => {
  if (!days?.from) {
    return '';
  }

  const from = dayjs(days.from.slice(0, 10)).format(DATE_PART);
  const to = days.to ? days.to.slice(0, 10) : null;

  return to && to > days.from.slice(0, 10) ? `${from} – ${dayjs(to).format(DATE_PART)}` : from;
};

/**
 * The calendar's props for one day ("YYYY-MM-DD"): `className` on a marked day, nothing otherwise. `DateTimeField` passes
 * its marked-day class (#137 — a light wash, bold and an accent ring: the #94 ring alone was too faint to notice; the
 * wash steps aside on the SELECTED day, which Mantine fills — a class, not an inline style, so it can).
 */
export const markedDayProps = (
  day: string,
  days: MarkedDays | null | undefined,
  className: string,
): { 'data-marked'?: boolean; className?: string } => {
  if (!days?.from) {
    return {};
  }

  const d = day.slice(0, 10);
  const from = days.from.slice(0, 10);
  const to = days.to ? days.to.slice(0, 10) : from;

  if (d < from || d > (to < from ? from : to)) {
    return {};
  }

  return { 'data-marked': true, className };
};

/**
 * ROADMAP #137 — a picker wall clock for the sentence under a deadline picker ("The event ends Oct 10, 2026, 9:00 PM."),
 * in the field's own date order and clock. Empty for no / an unreadable value.
 */
export const formatPickerMoment = (wall: string | null | undefined): string => {
  const at = wall ? dayjs(wall) : null;

  return at && at.isValid() ? at.format(`${DATE_PART}, ${clockFormat() === '12h' ? 'h:mm A' : 'HH:mm'}`) : '';
};

/**
 * True when the value is before "now" (minute granularity, or day when dateOnly) — the value read as a wall clock in
 * `tz` when given (an event's zone), else in the browser's.
 */
export const isPastDateTime = (value?: string | null, dateOnly = false, tz?: string | null): boolean => {
  if (!value) {
    return false;
  }

  if (!tz) {
    return dayjs(value).isBefore(dayjs(), dateOnly ? 'day' : 'minute');
  }

  const at = dayjs.tz(value, tz);
  const now = dayjs().tz(tz);

  return dateOnly ? at.format('YYYY-MM-DD') < now.format('YYYY-MM-DD') : at.isBefore(now, 'minute');
};
